// lib/exporters/cin7.ts
// Utility functions to export Product data in a Cin7-compatible CSV format
// NOTE – this is a first-pass mapping based on the current InventoryList export that Cin7 accepts.
// If Cin7 adds/changes any columns, update the HEADER constant below accordingly.

import { Product, ProductPricingTier } from "@/types";
import { exportToCSV } from "@/lib/utils/csv";

// Full header list copied from the InventoryList sample.
// For columns we don't yet map we will output an empty string so Cin7 keeps defaults.
const HEADER = [
  "ProductCode",
  "Name",
  "Category",
  "Brand",
  "Type",
  "FixedAssetType",
  "CostingMethod",
  "Length",
  "Width",
  "Height",
  "Weight",
  "CartonLength",
  "CartonWidth",
  "CartonHeight",
  "CartonInnerQuantity",
  "CartonQuantity",
  "CartonVolume",
  "WeightUnits",
  "DimensionUnits",
  "Barcode",
  "MinimumBeforeReorder",
  "ReorderQuantity",
  "DefaultLocation",
  "LastSuppliedBy",
  "SupplierProductCode",
  "SupplierProductName",
  "SupplierFixedPrice",
  // Price Tiers
  "PriceTier1",
  "PriceTier2",
  "PriceTier3",
  "PriceTier4",
  "PriceTier5",
  "PriceTier6",
  "PriceTier7",
  "PriceTier8",
  "PriceTier9",
  "PriceTier10",
  // misc flags
  "AssemblyBOM",
  "AutoAssemble",
  "AutoDisassemble",
  "DropShip",
  "DropShipSupplier",
  "AverageCost",
  "DefaultUnitOfMeasure",
  "InventoryAccount",
  "RevenueAccount",
  "ExpenseAccount",
  "COGSAccount",
  "ProductAttributeSet",
  "AdditionalAttribute1",
  "AdditionalAttribute2",
  "AdditionalAttribute3",
  "AdditionalAttribute4",
  "AdditionalAttribute5",
  "AdditionalAttribute6",
  "AdditionalAttribute7",
  "AdditionalAttribute8",
  "AdditionalAttribute9",
  "AdditionalAttribute10",
  "DiscountName",
  "ProductFamilySKU",
  "ProductFamilyName",
  "ProductFamilyOption1Name",
  "ProductFamilyOption1Value",
  "ProductFamilyOption2Name",
  "ProductFamilyOption2Value",
  "ProductFamilyOption3Name",
  "ProductFamilyOption3Value",
  "CommaDelimitedTags",
  "StockLocator",
  "PurchaseTaxRule",
  "SaleTaxRule",
  "Status",
  "Description",
  "ShortDescription",
  "Sellable",
  "PickZones",
  "AlwaysShowQuantity",
  "WarrantySetupName",
  "InternalNote",
  "ProductionBOM",
  "MakeToOrderBom",
  "QuantityToProduce",
  "IsAccountingDimensionEnabled",
  "DimensionAttribute1",
  "DimensionAttribute2",
  "DimensionAttribute3",
  "DimensionAttribute4",
  "DimensionAttribute5",
  "DimensionAttribute6",
  "DimensionAttribute7",
  "DimensionAttribute8",
  "DimensionAttribute9",
  "DimensionAttribute10",
  "HSCode",
  "CountryOfOrigin",
];

// Define the type for our CSV row data
type Cin7ProductRow = Record<string, string | number>;

// Helper to map our pricing tiers to Cin7 columns.
function mapPricingTiers(
  tiers: ProductPricingTier[] | undefined
): Record<string, string | number> {
  const tierColumns: Record<string, string | number> = {};
  // Initialise all PriceTier columns with 0
  for (let i = 1; i <= 10; i++) {
    tierColumns[`PriceTier${i}`] = 0;
  }
  if (!tiers || tiers.length === 0) return tierColumns;

  // Sort tiers by name (expects names like "Tier-1", "Tier-2" … or "Tier 1")
  const sorted = [...tiers].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );

  sorted.forEach((tier, idx) => {
    if (idx < 10) {
      tierColumns[`PriceTier${idx + 1}`] = tier.base_price ?? 0;
    }
  });
  return tierColumns;
}

export function mapProductToCin7(product: Product): Cin7ProductRow {
  const priceTierMap = mapPricingTiers(product.product_pricing_tiers);

  // Basic mapping – extends as needed
  const row: Cin7ProductRow = {
    ProductCode: product.part_number?.toUpperCase() || "",
    Name: product.title || "",
    Category: "", // you can populate if you have categories
    Brand: product.product_brand?.name || "",
    Type: product.status === "active" ? "Stock" : "Non Inventory",
    FixedAssetType: "",
    CostingMethod: "FIFO",
    Length: product.length ?? 0,
    Width: product.width ?? 0,
    Height: product.height ?? 0,
    Weight: product.weight ?? 0,
    CartonLength: "",
    CartonWidth: "",
    CartonHeight: "",
    CartonInnerQuantity: "",
    CartonQuantity: "",
    CartonVolume: "",
    WeightUnits: "kg",
    DimensionUnits: "mm",
    Barcode: "",
    MinimumBeforeReorder: 0,
    ReorderQuantity: 0,
    DefaultLocation: "Main Inventory",
    LastSuppliedBy: "",
    SupplierProductCode: "",
    SupplierProductName: "",
    SupplierFixedPrice: 0,
    ...priceTierMap,
    AssemblyBOM: "No",
    AutoAssemble: "No",
    AutoDisassemble: "No",
    DropShip: "No",
    DropShipSupplier: "",
    AverageCost: product.cost_price ?? 0,
    DefaultUnitOfMeasure: "Item",
    InventoryAccount: "", // fill when accounting integration ready
    RevenueAccount: "",
    ExpenseAccount: "",
    COGSAccount: "",
    ProductAttributeSet: product.editor ?? "",
    DiscountName: "",
    CommaDelimitedTags: product.tags?.map((t) => t.name).join(", ") || "",
    PurchaseTaxRule: "GST on Expenses",
    SaleTaxRule: product.status === "active" ? "GST on Income" : "",
    Status: product.status?.toUpperCase() || "DRAFT",
    Description: product.description || "",
    ShortDescription: product.meta_description || "",
    Sellable: product.status === "active" ? "Yes" : "No",
    AlwaysShowQuantity: 0,
    InternalNote: "",
  };

  // Ensure all headers exist in row with at least an empty string
  HEADER.forEach((col) => {
    if (!Object.prototype.hasOwnProperty.call(row, col)) {
      row[col] = "";
    }
  });

  return row;
}

export function exportProductsToCin7(
  products: Product[],
  filename: string
): void {
  const rows = products.map(mapProductToCin7);
  // exportToCSV flattens objects but we already have a flat mapping.
  // We pass header order explicitly to preserve Cin7 column sequence.
  exportToCSV(rows, filename, HEADER as (keyof Cin7ProductRow)[]);
}
